import React from 'react';
import { Paper, Skeleton, Typography, useTheme } from '@mui/material';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { currencyFormatter } from '../../../utils/currencyFormat';

const formatCompactCurrency = (value) => {
  if (value === null || value === undefined) {
    return '0';
  }

  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return '0';
  }

  const absValue = Math.abs(numeric);
  let scaledValue = numeric;
  let suffix = '';

  if (absValue >= 1_000) {
    scaledValue = Math.round((numeric / 1_000) * 10) / 10;
    suffix = 'K';
  }

  return suffix ? `${scaledValue}${suffix}` : scaledValue;
};

const SalesPurchaseTrendChart = ({
  data = [],
  loading = false,
  title = 'Sales & Purchase Trends',
  emptyMessage = 'No trend data available for the selected period.',
  totalSalesLabel = 'Total Sales',
  totalPurchasesLabel = 'Total Purchases',
  currencyCode = 'AED',
}) => {
  const formatter = currencyFormatter(currencyCode);
  const theme = useTheme();
  const isRTL = theme.direction === 'rtl';
  const hasChartData = data.length > 0 && data.some((point) => point.sales || point.purchases);

  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: 2,
        '& svg, & svg *, &, & *': {
          '&:focus, &:focus-visible': {
            outline: 'none !important',
            border: 'none !important',
          },
        },
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        {title}
      </Typography>

      {loading ? (
        <Skeleton variant="rounded" height={280} />
      ) : hasChartData ? (
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={data ?? []} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
            <CartesianGrid />
            <XAxis dataKey="label" fontSize={12} tickMargin={10} />
            <YAxis
              tickFormatter={(value) => `${formatCompactCurrency(value)}`}
              // In RTL, bidi/direction rules can cause tick text to "flip" visually and overlap the axis.
              tick={{ style: { direction: 'ltr', unicodeBidi: 'plaintext' } }}
              width={isRTL ? 120 : 80}
              fontSize={12}
              tickMargin={8}
            />
            <RechartsTooltip
              formatter={(value) => `${formatter.format(Number(value))}`}
              labelFormatter={(label) => label}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="sales"
              stroke={theme.palette.primary.main}
              strokeWidth={3}
              dot={{ r: 4 }}
              name={totalSalesLabel}
            />
            <Line
              type="monotone"
              dataKey="purchases"
              stroke={theme.palette.success.main}
              strokeWidth={3}
              dot={{ r: 4 }}
              name={totalPurchasesLabel}
            />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      )}
    </Paper>
  );
};

export default SalesPurchaseTrendChart;
